<template>
  <Authenticated>
    <CogsNav />
    <div class="site-menus-container">
      <section class="menu-recipes-list">
        <div class="header">
          <h2>Menu Recipes</h2>
          <div class="header-controls">
            <div class="gst-toggle-container">
              <label for="gst-toggle">GST Exclusive:</label>
              <ToggleSwitch :modelValue="showGSTExclusive" :modelName="'gst-toggle'" @update:modelValue="showGSTExclusive = $event" />
            </div>
          </div>
        </div>
        <div class="recipes-content">
          <div class="search-bar">
            <input type="search" placeholder="Search Menu Recipes..." v-model="menuRecipeQuery" />
          </div>
          <div class="recipe-filters">
            <select v-model="marginFilter">
              <option value="">All Margins</option>
              <option value="high">High Margin (>30%)</option>
              <option value="medium">Medium Margin (15-30%)</option>
              <option value="low">Low Margin (<15%)</option>
              <option value="none">No Margin Calculated</option>
            </select>
          </div>
          <ul class="recipes-list">
            <li v-for="(recipe, index) in filteredMenuRecipes" :key="recipe.name" class="menu-recipe-item">
              <div class="recipe-header">
                <div class="recipe-margin">
                  <span class="margin-value" v-if="getLiveMargin(recipe) !== null" :id="percentageIndicator(getLiveMargin(recipe))"> {{ getLiveMargin(recipe) }}% </span>
                  <span class="margin-value" v-else-if="recipe.value.percentage" :id="percentageIndicator(recipe.value.percentage)"> {{ recipe.value.percentage }}% </span>
                  <span class="margin-value" v-else id="unchecked">Edit to Calculate</span>
                </div>
                <div class="recipe-actions">
                  <button @click="loadFoundRecipe(recipe)" class="edit-btn"><font-awesome-icon icon="edit" /> Edit</button>
                </div>
              </div>
              <div class="recipe-details">
                <h3 class="recipe-name">{{ recipe.value.escaped }}</h3>
                <div class="recipe-info">
                  <span v-if="recipe.value.cost" class="recipe-cost"> Cost: <CurrencyFormatter :value="recipe.value.cost" :currency="companySettings.currency" /> </span>
                  <div v-if="recipe.value.price && recipe.value.price !== 'not_set'" class="price-container">
                    <span class="recipe-price">
                      Current price: <CurrencyFormatter :value="showGSTExclusive ? recipe.value.price / companyGST : recipe.value.price" :currency="companySettings.currency" />
                      <span class="gst-label">{{ showGSTExclusive ? "(excl. GST)" : "(incl. GST)" }}</span>
                    </span>
                    <div class="slider-container">
                      <div class="slider-row">
                        <input
                          type="range"
                          :id="`price-slider-${index}`"
                          :min="getSliderMin(recipe)"
                          :max="getSliderMax(recipe)"
                          step="0.01"
                          :value="livePrice(recipe)"
                          @input="updateLiveMargin(recipe, $event.target.value)" />
                        <button v-if="recipe.id in livePrices" type="button" class="reset-price" @click="delete livePrices[recipe.id]; marginTick++">
                          <font-awesome-icon icon="sync-alt" />
                        </button>
                        <span class="live-price" v-if="recipe.id in livePrices">
                          <CurrencyFormatter :value="showGSTExclusive ? livePrice(recipe) / companyGST : livePrice(recipe)" :currency="companySettings.currency" />
                          <span class="gst-label">{{ showGSTExclusive ? "excl." : "incl." }}</span>
                        </span>
                      </div>
                    </div>
                  </div>
                  <div v-else-if="recipe.value.price === 'not set'" class="price-not-set">
                    <span class="no-price-label">Price: Not Set</span>
                  </div>
                </div>
              </div>
            </li>
          </ul>
        </div>
      </section>
      <Recipe :currentRecipe="menuRecipe" @saved="onRecipeSaved" />
    </div>
  </Authenticated>
</template>

<script>
import Authenticated from "@/components/_layouts/Authenticated";
import Recipe from "./Recipe.vue";
import { cogsMixin } from "./shared/cogsMixin";
import { CurrencyFormatter, ToggleSwitch } from "@monolith/components";
import CogsNav from "./shared/CogsNav.vue";

export default {
  name: "COGSSiteMenus",
  components: { Authenticated, Recipe, CurrencyFormatter, ToggleSwitch, CogsNav },
  mixins: [cogsMixin],
  data() {
    return {
      menuRecipe: {},
      menuRecipeQuery: "",
      marginFilter: "",
      categoryFilter: "",
      check: null,
      marginTick: 0,
      showGSTExclusive: true,
      livePrices: {},
    };
  },
  computed: {
    filteredMenuRecipes() {
      let filtered = this.cogsMenuRecipes;

      if (this.menuRecipeQuery) {
        const query = this.menuRecipeQuery.toLowerCase();
        filtered = filtered.filter((recipe) => recipe.value.escaped.toLowerCase().includes(query));
      }

      if (this.marginFilter) {
        filtered = filtered.filter((recipe) => {
          const percentage = recipe.value.percentage;

          if (this.marginFilter === "none") {
            return !percentage || percentage === "not_calculated";
          }

          if (!percentage || percentage === "not_calculated") {
            return false;
          }

          const margin = parseFloat(percentage);
          switch (this.marginFilter) {
            case "high":
              return margin >= 30;
            case "medium":
              return margin >= 15 && margin < 30;
            case "low":
              return margin < 15;
            default:
              return true;
          }
        });
      }
      return filtered;
    },

  },
  methods: {
    loadFoundRecipe(productName) {
      this.menuRecipe = productName;
    },

    percentageIndicator(value) {
      let indicator = "";
      if (value === null || value === undefined || value === "not_calculated") {
        return;
      } else {
        const percentage = parseFloat(value);
        if (percentage >= 30) {
          indicator = "high";
        } else if (percentage >= 15) {
          indicator = "medium";
        } else {
          indicator = "low";
        }
      }
      return indicator;
    },

    onRecipeSaved() {
      this.menuRecipe = {};
    },

    getLiveMargin(recipe) {
      void this.marginTick;
      const inclPrice = this.livePrice(recipe);
      if (!recipe.value.cost || !inclPrice || inclPrice <= 0) return null;

      const exclPrice = inclPrice / this.companyGST;
      const cost = parseFloat(recipe.value.cost);

      if (exclPrice <= cost) return 0;
      return ((cost / exclPrice) * 100).toFixed(2);
    },

    updateLiveMargin(recipe, newPrice) {
      this.livePrices[recipe.id] = parseFloat(newPrice);
      this.marginTick++;
    },

    livePrice(recipe) {
      return recipe.id in this.livePrices ? this.livePrices[recipe.id] : parseFloat(recipe.value.price);
    },

    getSliderMin(recipe) {
      if (recipe.value.cost) {
        // slider value is always GST-inclusive, so min is the inclusive break-even
        return (parseFloat(recipe.value.cost) * this.companyGST).toFixed(2);
      }
      return 0;
    },

    getSliderMax(recipe) {
      const price = parseFloat(recipe.value.price);
      return price ? (price + 10).toFixed(2) : (100).toFixed(2);
    },
  },

  watch: {
    getCogsSelectedSite: function () {
      if (this.getCogsSelectedSite) this.calculateMenuRecipePercentages();
    },
  },

  mounted() {
    if (this.getCogsSelectedSite) this.calculateMenuRecipePercentages();

    if (this.$route.params.loadProduct && this.$route.params.recipeId) {
      this.$nextTick(() => {
        const recipe = this.cogsMenuRecipes.find((r) => r.id === this.$route.params.recipeId);
        if (recipe) {
          this.loadFoundRecipe(recipe);
        }
      });
    }
  },
};
</script>

<style lang="scss" scoped>
.site-menus-container {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: flex-start;
  padding: 1rem;
  gap: 1rem;
}

.menu-recipes-list {
  background-color: var(--primary-colour);
  border-radius: 1rem;
  box-shadow: var(--box-shadow);
  padding: 0.5rem 1rem;
  min-width: 50%;

  .header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 0;

    .header-controls {
      display: flex;
      align-items: center;
      gap: 1rem;

      .gst-toggle-container {
        display: flex;
        align-items: center;
        gap: 0.5rem;

        label {
          font-weight: bold;
          color: var(--text-colour);
          white-space: nowrap;
        }
      }
    }
  }

  .recipes-content {
    padding: 1rem;

    .search-bar {
      margin-bottom: 1rem;

      input {
        width: 100%;
        padding: 0.5rem;
        box-shadow: var(--box-shadow);
        border: 1px solid var(--secondary-colour);
        border-radius: 0.25rem;
        background-color: var(--opacity-background);
        color: var(--action-colour);
      }
    }

    .recipe-filters {
      display: flex;
      gap: 1rem;
      margin-bottom: 1rem;

      select {
        flex: 1;
        padding: 0.5rem;
        box-shadow: var(--box-shadow);
        border: none;
        border-radius: 0.25rem;
      }
    }

    .recipes-list {
      max-height: 60vh;
      overflow-y: auto;
      padding: 0.5rem;

      .menu-recipe-item {
        display: flex;
        flex-direction: row-reverse;
        justify-content: space-between;
        list-style: none;
        margin-bottom: 1rem;
        padding: 1rem;
        border: 1px solid var(--border-colour);
        border-radius: 5px;
        background-color: var(--opacity-background);

        .recipe-header {
          cursor: pointer;
          display: flex;
          flex-direction: column;
          justify-content: space-between;
          align-items: flex-end;
          gap: 0.75rem;

          .recipe-actions {
            display: flex;

            button {
              padding: 0.5rem 1.5rem;
              margin: 0;
              border: none;
              border-radius: 3px;
              cursor: pointer;
              font-size: 0.8rem;
              display: flex;
              align-items: center;
            }
          }

          .recipe-margin {
            .margin-value {
              padding: 0.3rem 0.6rem;
              border-radius: 3px;
              font-weight: bold;

              &#low {
                background-color: rgba(40, 167, 69, 0.8);
                color: var(--secondary-text-colour);
              }

              &#medium {
                background-color: rgba(255, 193, 7, 0.8);
                color: var(--secondary-text-colour);
              }

              &#high {
                background-color: rgba(161, 19, 19, 0.8);
                color: var(--text-colour);
              }

              &#unchecked {
                color: var(--action-colour);
                padding: 0.3rem 0;
              }
            }
          }
        }

        .recipe-details {
          .recipe-name {
            color: var(--action-colour);
            margin: 0;
          }
          .recipe-info {
            margin-bottom: 0.5rem;

            .recipe-cost,
            .recipe-price {
              margin-right: 1rem;
              font-size: 0.9rem;
            }

            .recipe-cost {
              color: var(--danger-colour);
            }

            .price-container {
              .recipe-price {
                display: block;
                font-size: 0.85rem;
                color: var(--text-colour);
                margin-bottom: 0.25rem;

                .gst-label {
                  font-size: 0.7rem;
                  color: var(--action-colour);
                  font-style: italic;
                  margin-left: 0.25rem;
                }
              }

              .slider-container {
                margin-top: 0.5rem;

                .slider-row {
                  display: flex;
                  align-items: center;
                  gap: 0.75rem;
                }

                .reset-price {
                  background: none;
                  border: none;
                  cursor: pointer;
                  color: var(--warning-colour);
                  padding: 0 0.25rem;
                  font-size: 0.75rem;
                }

                .live-price {
                  white-space: nowrap;
                  font-size: 0.9rem;
                  font-weight: 600;
                  color: var(--text-colour);
                  min-width: 5rem;

                  .gst-label {
                    font-size: 0.7rem;
                    color: var(--action-colour);
                    font-style: italic;
                    margin-left: 0.2rem;
                  }
                }

                input[type="range"] {
                  width: 100%;
                  margin: 0.25rem 0;
                  -webkit-appearance: none;
                  height: 6px;
                  border-radius: 3px;
                  background: var(--border-colour);
                  outline: none;

                  &::-webkit-slider-thumb {
                    -webkit-appearance: none;
                    appearance: none;
                    width: 18px;
                    height: 18px;
                    border-radius: 50%;
                    background: var(--action-colour);
                    cursor: pointer;
                    border: 2px solid var(--secondary-colour);
                  }

                  &::-moz-range-thumb {
                    width: 18px;
                    height: 18px;
                    border-radius: 50%;
                    background: var(--action-colour);
                    cursor: pointer;
                    border: 2px solid var(--secondary-colour);
                  }
                }

                .slider-labels {
                  display: flex;
                  justify-content: space-between;
                  font-size: 0.7rem;
                  color: var(--secondary-text-colour);
                  margin-top: 0.25rem;
                }
              }
            }

            .price-not-set {
              .no-price-label {
                color: var(--danger-colour);
                margin-right: 1rem;
                font-size: 0.9rem;
                font-style: italic;
              }
            }
          }

          .recipe-status {
            margin-bottom: 0.75rem;
            font-size: 0.85rem;

            .status-warning {
              color: var(--warning-colour);
              margin-right: 1rem;
            }

            .status-error {
              color: var(--danger-colour);
              margin-right: 1rem;
            }
          }

          .recipe-composition {
            margin-bottom: 0.75rem;
            font-size: 0.9rem;
            color: var(--secondary-text-colour);

            .ingredients-count,
            .subrecipes-count {
              margin-right: 1rem;
            }
          }
        }
      }
    }
  }
}

@media only screen and (orientation: portrait) {
  .site-menus-container {
    .menu-section {
      .content-wrapper {
        flex-direction: column;

        .site-menu-controls {
          min-width: 95%;
          max-width: 95%;
          margin: 1rem auto;

          .menu-categories {
            height: 40vh;

            .category-details {
              .category-products {
                max-height: 20vh;
              }
            }
          }
        }
      }
    }

    .menu-recipes-list {
      .recipes-content {
        .recipe-filters {
          flex-direction: column;
        }

        .menu-recipes-list {
          height: 40vh;
        }
      }
    }
  }
}
</style>
